import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Container, Nav, Tab } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { Link, useHistory, useLocation } from "react-router-dom";
import type { Objection } from "@/briefpoint-client/models";
import Loading from "@/components/Loading";
import PageHeader from "@/components/PageHeader/PageHeader";
import { useObjectionApi, useUserApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useMiscContent from "@/hooks/useMiscContent";
import { getObjectionsOptions } from "@/lib/query-options";
import type { UserResponse } from "@/lib/types";
import DefinitionsTable from "./DefinitionsTable";
import GeneralObjections from "./GeneralObjections";
import Intro from "./Intro";
import styles from "./Library.module.scss";
import ObjectionTable from "./ObjectionTable";
import PreliminaryStatement from "./PreliminaryStatement";
import QuestionsTable from "./QuestionsTable";

export default function Library() {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const userApi = useUserApi();
  const tab = new URLSearchParams(location.search).get("tab");
  const type = new URLSearchParams(location.search).get("type");
  const [
    preliminaryStatements,
    generalObjections,
    intros,
    ,
    questions,
    updateMiscContent,
  ] = useMiscContent();
  const objectionApi = useObjectionApi();
  const objectionsQuery = useQuery({
    ...getObjectionsOptions(objectionApi),
  });
  const [users, setUsers] = useState<Map<string, UserResponse>>(new Map());
  const queryRef = useRef<Objection[] | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch setUsers
  useEffect(() => {
    const fetchData = async () => {
      if (
        !user ||
        !objectionsQuery.data ||
        (queryRef.current && queryRef.current === objectionsQuery.data)
      ) {
        return;
      }

      queryRef.current = objectionsQuery.data;
      if (user.id && !users.has(user.id)) {
        setUsers((prev) => new Map([...prev, [user.id, user]]));
      }

      let variantOwnerIds: string[] = objectionsQuery.data
        .flatMap((x) => x.variants)
        .filter((x) => x.ownerId && x.ownerId !== user?.id)
        .map((x) => x.ownerId as string);
      variantOwnerIds = [...new Set(variantOwnerIds)]; // dedupe the users list

      for (const id of variantOwnerIds) {
        if (!users.has(id)) {
          const otherUser = (await userApi.userGetUser({
            userId: id,
          })) as UserResponse;

          if (otherUser && queryRef.current === objectionsQuery.data) {
            setUsers((prev) => new Map([...prev, [id, otherUser]]));
          }
        }
      }
    };

    if (objectionsQuery.data && user) {
      fetchData();
    }
  }, [objectionsQuery.data, user, userApi, setUsers, users]);

  function setUrlWithType(newType: string | null, newTab: string | null) {
    if (newType || newTab) {
      history.push({
        pathname: "/library",
        search: `?type=${newType ?? type}${newTab ? `&tab=${newTab}` : ""}`,
      });
    }
  }

  return (
    <Container className={`page-container ${styles.libraryContainer}`}>
      <Tab.Container
        id="library-type"
        activeKey={type || "responses"}
        onSelect={(k) => setUrlWithType(k, null)}
      >
        <Nav className={styles.libraryTypeNav}>
          <Nav.Item>
            <Nav.Link eventKey="responses">Discovery Responses</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="requests">Discovery Requests</Nav.Link>
          </Nav.Item>
        </Nav>
        <div className={styles.navBorder} />
        <Tab.Content className={styles.typeContent}>
          <Tab.Pane eventKey="responses">
            <PageHeader
              testId="discovery-responses-header"
              title="Discovery Responses"
            />
            <Tab.Container
              id="left-tabs-example"
              activeKey={tab || "objections"}
              onSelect={(k) => setUrlWithType("responses", k)}
            >
              <Nav className={styles.libraryNav}>
                <Nav.Item>
                  <Nav.Link eventKey="objections">Objections</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="responses">Responses</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="preliminary-statement">
                    Preliminary Statements
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="general-objections">
                    General Objections
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <div className={`${styles.navBorder} ${styles.navBorderLarge}`} />
              <Tab.Content>
                <Tab.Pane eventKey="objections">
                  <Loading isLoading={objectionsQuery.isFetching}>
                    <ObjectionTable
                      objections={objectionsQuery.data ?? []}
                      isResponses={false}
                    />
                  </Loading>
                </Tab.Pane>
                <Tab.Pane eventKey="responses">
                  <Loading isLoading={!objectionsQuery.data}>
                    <ObjectionTable
                      objections={objectionsQuery.data ?? []}
                      isResponses={true}
                    />
                  </Loading>
                </Tab.Pane>
                <Tab.Pane eventKey="preliminary-statement" unmountOnExit>
                  <Loading isLoading={!preliminaryStatements}>
                    {preliminaryStatements && (
                      <PreliminaryStatement
                        preliminaryStatements={preliminaryStatements}
                        onUpdate={updateMiscContent}
                      />
                    )}
                  </Loading>
                </Tab.Pane>
                <Tab.Pane eventKey="general-objections">
                  <Loading isLoading={!generalObjections}>
                    {generalObjections && (
                      <GeneralObjections
                        generalObjections={generalObjections}
                        onUpdate={updateMiscContent}
                      />
                    )}
                  </Loading>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Tab.Pane>
          <Tab.Pane eventKey="requests">
            <h1 data-testid="discovery-requests-header">
              Discovery Requests (Beta
              <span className={styles.infoLink}>
                <Link
                  to="/tutorials#discovery_requests"
                  title="Learn More About Briefpoint's Propounding Discovery Beta"
                  target="_blank"
                >
                  <InfoCircle />
                </Link>
              </span>
              )
            </h1>
            <Tab.Container
              id="left-tabs-example"
              activeKey={tab || "requests"}
              onSelect={(k) => setUrlWithType("requests", k)}
            >
              <Nav className={styles.libraryNav}>
                <Nav.Item>
                  <Nav.Link eventKey="requests">Questions</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="intro">Intro</Nav.Link>
                </Nav.Item>
                {/* <Nav.Item>
                        <Nav.Link eventKey="instructions">Instructions</Nav.Link>
                      </Nav.Item> */}
                <Nav.Item>
                  <Nav.Link eventKey="definitions">Definitions</Nav.Link>
                </Nav.Item>
              </Nav>
              <div className={`${styles.navBorder} ${styles.navBorderLarge}`} />
              <Tab.Content>
                <Tab.Pane eventKey="intro" unmountOnExit>
                  <Loading isLoading={!intros}>
                    <Intro intros={intros ?? []} onUpdate={updateMiscContent} />
                  </Loading>
                </Tab.Pane>
                <Tab.Pane eventKey="definitions">
                  <DefinitionsTable />
                </Tab.Pane>
                <Tab.Pane eventKey="requests">
                  <Loading isLoading={!questions}>
                    <QuestionsTable />
                  </Loading>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
}
